import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useClassPerfExams } from '../../../api/hooks';
import type { ClassPerfExamRow, ExamScope } from '../../../api/types';
import { ScopeCheckboxes, DEFAULT_SCOPES } from '../../../lib/scopes';
import { Badge, Chip, PagedFooter, PanelCard, SortableTh, Table, Td, Term } from '../../../components/ui';
import Spinner from '../../../components/ui/Spinner';
import ErrorPanel from '../../../components/ui/ErrorPanel';
import EmptyState from '../../../components/ui/EmptyState';
import { FaClipboardList } from '../../../components/ui/icons';
import { useSortableTable, SortColumn } from '../../../lib/useSortableTable';
import { usePagination } from '../../../lib/usePagination';
import { dayMonth, signed1, deltaTone } from '../lib/cpBits';
import { TH_RULE } from '../../../lib/tableRules';

interface Props {
  gradeId: number;
  sectionId: number | 'all';
  subjectId: number | 'all';
  weeks?: number;
  /** "Grade 10 · all sections", for the hint under the title. */
  ctx?: string;
  /** "Mathematics" or "All subjects". */
  subjLabel?: string;
}

const EXAMS_COLUMNS: SortColumn<ClassPerfExamRow>[] = [
  { key: 'date', accessor: (e) => e.created, type: 'date' },
  { key: 'exam', accessor: (e) => e.name },
  { key: 'subject', accessor: (e) => e.subject?.name },
  { key: 'scope', accessor: (e) => e.scope },
  { key: 'students', accessor: (e) => e.students_took, type: 'number' },
  { key: 'avg', accessor: (e) => e.avg_score, type: 'number' },
  { key: 'avg_time', accessor: (e) => e.avg_duration_minutes, type: 'number' },
  { key: 'delta', accessor: (e) => e.delta_vs_prior_on_same_subject, type: 'number' },
];

const SCOPE_TONE = { school: 'info', national: 'accent', practice: 'neutral' } as const;
const SCOPE_LABEL = { school: 'School', national: 'National', practice: 'Practice' } as const;

export default function ExamsTab({ gradeId, sectionId, subjectId, weeks, ctx, subjLabel }: Props) {
  const [scopes, setScopes] = useState<ExamScope[]>([...DEFAULT_SCOPES]);
  const q = useClassPerfExams({
    grade: gradeId, section: sectionId, subject: subjectId, scopes, weeks,
  });
  const { rows, sort, toggle } = useSortableTable(q.data?.exams, EXAMS_COLUMNS, { key: 'date', dir: 'desc' });

  const pg = usePagination(rows, 10, `${sort?.key}${sort?.dir}${scopes.join()}`);
  const hint = [ctx, subjLabel, 'newest first'].filter(Boolean).join(' · ');

  return (
    <PanelCard title="Exams taken by this class" help="tab_exams" align="end" subtitle={hint}
      actions={<ScopeCheckboxes value={scopes} onChange={setScopes} />}>
      {q.isLoading ? (
        <div className="flex justify-center py-8"><Spinner size="md" /></div>
      ) : q.isError || !q.data ? (
        <ErrorPanel />
      ) : q.data.exams.length === 0 ? (
        <EmptyState
          icon={<FaClipboardList />}
          title="No exams yet"
          body={`No exams in this window${subjLabel ? ` for ${subjLabel}` : ''}. Try another scope or a wider window.`}
        />
      ) : (
        <Table minWidth={860}>
          <thead>
            <tr>
              <SortableTh columnKey="date" sort={sort} onToggle={toggle} className={TH_RULE}>Date</SortableTh>
              <SortableTh columnKey="exam" sort={sort} onToggle={toggle} className={`${TH_RULE} min-w-[12rem]`}>Exam</SortableTh>
              <SortableTh columnKey="subject" sort={sort} onToggle={toggle} className={TH_RULE}>Subject</SortableTh>
              <SortableTh columnKey="scope" sort={sort} onToggle={toggle} help="scope" className={TH_RULE}>Scope</SortableTh>
              <SortableTh columnKey="students" sort={sort} onToggle={toggle} align="right" className={TH_RULE} help="Students in the cohort who took this exam.">Students</SortableTh>
              <SortableTh columnKey="avg" sort={sort} onToggle={toggle} align="right" className={TH_RULE} help="The cohort's average score on this exam.">Avg</SortableTh>
              <SortableTh columnKey="avg_time" sort={sort} onToggle={toggle} align="right" className={TH_RULE} help="avg_time">Avg time</SortableTh>
              <SortableTh columnKey="delta" sort={sort} onToggle={toggle} align="right" className={TH_RULE} help="vs_prior">Vs prior</SortableTh>
            </tr>
          </thead>
          <tbody>
            {pg.pageItems.map((e) => (
              <tr key={e.id}>
                <Td className="whitespace-nowrap font-mono font-medium text-muted">{dayMonth(e.created)}</Td>
                <Td>
                  <div className="flex flex-wrap items-center gap-2">
                    <Link className="font-semibold text-ink no-underline hover:text-muted-2"
                          to={`/exams/report/${e.id}${typeof sectionId === 'number' ? `?section=${sectionId}` : ''}`}>
                      {e.name}
                    </Link>
                    {e.delivery === 'paper' && (
                      <Badge tone="neu" size="sm"><Term k="delivery">Paper</Term></Badge>
                    )}
                  </div>
                </Td>
                <Td>{e.subject?.name ?? '—'}</Td>
                <Td><Badge tone={SCOPE_TONE[e.scope]} size="sm">{SCOPE_LABEL[e.scope]}</Badge></Td>
                <Td align="right" className="font-mono font-medium tabular-nums">{e.students_took}</Td>
                <Td align="right" className="font-mono font-semibold tabular-nums">{e.avg_score.toFixed(1)}%</Td>
                <Td align="right" className="whitespace-nowrap font-mono font-medium tabular-nums text-muted">
                  {e.avg_duration_minutes == null ? '—' : `${e.avg_duration_minutes.toFixed(1)} min`}
                </Td>
                <Td align="right">
                  {e.delta_vs_prior_on_same_subject == null
                    ? <span className="text-muted">—</span>
                    : (
                      <Chip tone={deltaTone(e.delta_vs_prior_on_same_subject)}>
                        {signed1(e.delta_vs_prior_on_same_subject)}
                      </Chip>
                    )}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {!q.isLoading && q.data && q.data.exams.length > 0 && <PagedFooter className="mt-3.5" label="Exams pagination" {...pg} onPage={pg.setPage} />}
    </PanelCard>
  );
}
